import { useState } from 'react';
import { Dex360Audit } from '@/api/dex360-audit';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { TableSheet } from '@/components/ui/custom/table-sheet';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { format } from 'date-fns/format';
import { ArrowDown, ArrowUp, ArrowUpDown, Eye, EyeOff } from 'lucide-react';

import { Dex360AuditDetails } from './details';

export const getColumns = (
	handleSingleHide: ((id: number) => void) | undefined,
	isActiveTab: boolean,
	sorting: SortingState,
	setSorting: React.Dispatch<React.SetStateAction<SortingState>>,
): ColumnDef<Dex360Audit>[] => {
	const sortBy = sorting?.[0]?.id ?? '';
	const sortDirection = sorting?.[0]?.desc ? 'desc' : 'asc';

	const handleSort = (field: string) => {
		setSorting((current) => {
			if (current.length > 0 && current[0].id === field) {
				// Toggle sort direction
				return [{ id: field, desc: !current[0].desc }];
			}
			// Default to ascending
			return [{ id: field, desc: false }];
		});
	};

	const renderSortIcon = (field: string) => {
		if (sortBy !== field)
			return <ArrowUpDown className="ml-1 h-4 w-4 opacity-40" />;
		return sortDirection === 'asc' ? (
			<ArrowUp className="ml-1 h-4 w-4 text-blue-500" />
		) : (
			<ArrowDown className="ml-1 h-4 w-4 text-blue-500" />
		);
	};

	const summarizeLocations = (arr?: string[] | null, maxVisible = 2): string => {
		const list = (arr ?? []).map((s) => s.trim()).filter(Boolean);
		return list.length > maxVisible
			? `${list.slice(0, maxVisible).join(', ')} + ${list.length - maxVisible}`
			: list.join(', ');
	};

	return [
		{
			id: 'select',
			header: ({ table }) => (
				<div className="flex justify-center pl-4">
					<Checkbox
						checked={table.getIsAllPageRowsSelected()}
						onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
						aria-label="Select all"
					/>
				</div>
			),
			cell: ({ row }) => (
				<Checkbox
					className="mx-auto"
					checked={row.getIsSelected()}
					onCheckedChange={(value) => row.toggleSelected(!!value)}
					aria-label="Select row"
				/>
			),
			enableSorting: false,
			enableHiding: false,
		},
		{
			accessorKey: 'id',
			header: () => <div className="ml-4">Id</div>,
			cell: ({ row }) => <div>{row.original.id}</div>,
		},
		{
			accessorKey: 'accountId',
			header: () => <div className="ml-4">Account ID</div>,
			cell: ({ row }) => <div>{row.original.accountId}</div>,
		},
		{
			accessorKey: 'locationsDisplay',
			header: () => <div className="ml-4">Locations</div>,
			cell: ({ row }) => (
				<div>{summarizeLocations(row.original.locations) || ''}</div>
			),
		},
		{
			accessorKey: 'producerName',
			header: () => <div className="ml-4">Producer Name</div>,
			cell: ({ row }) => <div>{row.original.producerName}</div>,
		},
		{
			accessorKey: 'status',
			header: () => <div className="ml-4">Status</div>,
			cell: ({ row }) => <div>{row.original.status}</div>,
		},
		{
			accessorKey: 'error',
			header: () => (
				<Button variant="ghost" onClick={() => handleSort('error')}>
					Error {renderSortIcon('error')}
				</Button>
			),
			cell: ({ row }) => <div>{row.original.error}</div>,
		},
		{
			accessorKey: 'errorDate',
			header: () => (
				<Button variant="ghost" onClick={() => handleSort('errorDate')}>
					Error Date {renderSortIcon('errorDate')}
				</Button>
			),
			cell: ({ row }) => {
				const date = row.original.errorDate
					? format(new Date(row.original.errorDate), 'P')
					: '---';

				return (
					<div className="flex items-center justify-between">
						{date}
					</div>
				);
			},
		},
		...(isActiveTab ? [{
			id: 'hide',
			header: () => <div className="ml-2 whitespace-nowrap">Hide Row</div>,
			cell: ({ row }) => {
				return (
					<div className="flex items-center justify-between ml-2">
						{handleSingleHide && row.original.id != null && (
							<Button
								variant="ghost"
								size="icon"
								onClick={() => handleSingleHide(row.original.id!)}
								className="group relative"
								title="Hide this row"
							>
								<span className="relative inline-block h-4 w-4">
									<Eye
										size={16}
										className="absolute inset-0 transition-opacity duration-200 group-hover:opacity-0"
									/>
									<EyeOff
										size={16}
										className="absolute inset-0 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
									/>
								</span>
							</Button>
						)}
					</div>
				);
			},
		} as ColumnDef<Dex360Audit>] : []),
		{
			id: 'details',
			header: () => <div className="ml-2">Details</div>,
			cell: ({ row }) => <Dex360AuditDetails audit={row.original} />,
			enableSorting: false,
		},
	];
};
